
import { isAfter, isBefore, addMinutes } from "date-fns";

export interface BusySlot {
  start: Date;
  end: Date;
  summary: string;
}

/**
 * Checks if a slot overlaps with any busy periods
 */
export function checkSlotOverlap(
  slotTime: Date, 
  slotEndTime: Date, 
  busySlots: BusySlot[]
): { isOverlapping: boolean; overlapEvent: any } {
  let isOverlapping = false;
  let overlapEvent = null;
  
  for (const busy of busySlots) {
    // If the slot starts during a busy period
    const slotStartsDuringBusy = 
      isAfter(slotTime, busy.start) && 
      isBefore(slotTime, busy.end);
    
    // If the slot ends during a busy period
    const slotEndsDuringBusy = 
      isAfter(slotEndTime, busy.start) && 
      isBefore(slotEndTime, busy.end);
    
    // If the slot completely contains a busy period
    const slotContainsBusy = 
      isBefore(slotTime, busy.start) && 
      isAfter(slotEndTime, busy.end);
      
    // If busy period completely contains the slot
    const busyContainsSlot =
      (isBefore(busy.start, slotTime) || busy.start.getTime() === slotTime.getTime()) &&
      (isAfter(busy.end, slotEndTime) || busy.end.getTime() === slotEndTime.getTime());
    
    if (slotStartsDuringBusy || slotEndsDuringBusy || slotContainsBusy || busyContainsSlot) {
      isOverlapping = true;
      overlapEvent = busy;
      break;
    }
  }
  
  return { isOverlapping, overlapEvent };
}

/**
 * Checks if a slot falls within buffer periods of any busy slots
 */
export function checkBufferConflicts(
  slotTime: Date,
  slotEndTime: Date,
  busySlots: BusySlot[],
  bufferBefore: number,
  bufferAfter: number
): { isInBuffer: boolean; bufferEvent: any } {
  let isInBuffer = false;
  let bufferEvent = null;
  
  if (!bufferBefore && !bufferAfter) {
    return { isInBuffer, bufferEvent };
  }
  
  for (const busy of busySlots) {
    // Calculate buffer start and end times
    const bufferStart = bufferBefore ? 
      addMinutes(busy.start, -bufferBefore) : busy.start;
    const bufferEnd = bufferAfter ? 
      addMinutes(busy.end, bufferAfter) : busy.end;
    
    // Check if slot falls within any buffer period
    if ((isAfter(slotTime, bufferStart) && isBefore(slotTime, busy.start)) ||
        (isAfter(slotTime, busy.end) && isBefore(slotTime, bufferEnd)) ||
        (isAfter(slotEndTime, bufferStart) && isBefore(slotEndTime, bufferEnd)) ||
        (isAfter(slotTime, bufferStart) && isBefore(slotTime, bufferEnd))) {
      isInBuffer = true;
      bufferEvent = { ...busy, summary: `Buffer for ${busy.summary}` };
      break;
    }
  }
  
  return { isInBuffer, bufferEvent };
}
